import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Plus, 
  CheckCircle2, 
  Circle, 
  Target, 
  Flame,
  Trophy,
  Settings,
  LogOut,
  Menu,
  X,
  HelpCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useHabits } from '../contexts/HabitsContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import CreateHabitModal from './CreateHabitModal';
import HabitDetailsModal from './HabitDetailsModal';
import CompleteHabitModal from './CompleteHabitModal';
import SuccessToast from './SuccessToast';
import HelpTooltip from './HelpTooltip';
import AppTour from './AppTour';
import HabitDetailsTour from './HabitDetailsTour';

import { Habit } from '../types';
import styles from './Dashboard.module.css';

const Dashboard: React.FC = () => {
  const { userProfile, logout } = useAuth();  const { 
    habits, 
    habitStacks, 
    isHabitCompletedToday, 
    isStackCompletedToday,
    completeHabit,
    uncompleteHabit,
    getBestCurrentStreak,
    getCompletionRate
  } = useHabits();const [showSidebar, setShowSidebar] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedHabit, setSelectedHabit] = useState<Habit | null>(null);
  const [showHabitDetails, setShowHabitDetails] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [habitToComplete, setHabitToComplete] = useState<Habit | null>(null);
  const [isLargeScreen, setIsLargeScreen] = useState(false);  const [showSuccessToast, setShowSuccessToast] = useState(false);  const [successHabitName, setSuccessHabitName] = useState('');
  const [isCompletingHabit, setIsCompletingHabit] = useState(false);  const [showTour, setShowTour] = useState(false);
  const [showHabitDetailsTour, setShowHabitDetailsTour] = useState(false);

  // Check if any modal is open
  const isAnyModalOpen = showAddModal || showHabitDetails || showCompleteModal || showTour || showHabitDetailsTour;

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const isNowLargeScreen = window.innerWidth >= 1024;
      setIsLargeScreen(isNowLargeScreen);
      
      // Auto-fechar sidebar quando mudar para desktop
      if (isNowLargeScreen && showSidebar) {
        setShowSidebar(false);
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [showSidebar]); // 🔧 Adicionar showSidebar como dependência
  
  const today = new Date();
  const todayFormatted = format(today, "EEEE, d 'de' MMMM", { locale: ptBR });  const handleCompleteHabit = async (habit: Habit, customValue?: string | number, notes?: string) => {
    // Prevenir múltiplos cliques se já estiver completando
    if (isCompletingHabit) return;
    
    try {
      console.log('🟡 Dashboard: Iniciando handleCompleteHabit para:', habit.name);
      setIsCompletingHabit(true);
      await completeHabit(habit.id, undefined, notes, customValue);
      
      // Mostrar toast de sucesso
      setSuccessHabitName(habit.name);
      setShowSuccessToast(true);
      
      console.log('✅ Dashboard: handleCompleteHabit finalizado com sucesso');
      
    } catch (error) {
      console.error('❌ Dashboard: Erro ao completar hábito:', error);
    } finally {
      // Permitir novas ações após 2.5 segundos (som + animação)
      setTimeout(() => {
        setIsCompletingHabit(false);
      }, 2500);
    }
  };

  const openCompleteModal = (habit: Habit) => {
    console.log('🟡 Dashboard: openCompleteModal chamado para:', habit.name, 'hasCustomValue:', habit.hasCustomValue);
    if (habit.hasCustomValue) {
      console.log('🟡 Dashboard: Abrindo modal customizado');
      setHabitToComplete(habit);
      setShowCompleteModal(true);
    } else {
      console.log('🟡 Dashboard: Completando diretamente');
      handleCompleteHabit(habit);
    }
  };

  const openHabitDetails = (habit: Habit) => {
    setSelectedHabit(habit);
    setShowHabitDetails(true);
  };

  const getGreeting = () => {
    const hour = today.getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };
  const getDailyAgenda = () => {
    const agenda: any[] = [];
    
    // Individual habits
    habits.forEach(habit => {
      agenda.push({
        id: habit.id,
        type: 'habit',
        name: habit.name,
        description: habit.description,
        duration: habit.duration,
        isCompleted: isHabitCompletedToday(habit.id),
        habit
      });
    });

    // Habit stacks
    habitStacks.forEach(stack => {
      agenda.push({
        id: stack.id,
        type: 'stack',
        name: stack.name,
        description: `Após ${stack.triggerHabit}`,
        duration: stack.newHabit.duration,
        isCompleted: isStackCompletedToday(stack.id),
        stack
      });
    });

    return agenda.sort((a: any, b: any) => a.name.localeCompare(b.name));
  };
  const agenda = getDailyAgenda();
  const completedToday = agenda.filter(item => item.isCompleted).length;
  const totalItems = agenda.length;
  const completionPercentage = getCompletionRate(); // Usar a função do contexto
  const currentStreak = getBestCurrentStreak(); // Usa a maior sequência para o dashboard
  return (
    <div className={styles.container}>      {/* Mobile Header */}      <div className={`${styles.mobileHeader} ${isLargeScreen ? styles.mobileHeaderHidden : styles.mobileHeaderVisible}`}>        <div className={styles.headerContent}>
          <h1 className={styles.appTitle}>Axioma Hábitos</h1>
        </div>        <button
          onClick={() => {
            setShowSidebar(!showSidebar);
          }}
          className={styles.menuButton}
        >
          <Menu className="w-6 h-6 text-gray-700" />
        </button>
      </div>      <div className={styles.layout}>        {/* Sidebar */}
        <aside 
          className={`${styles.sidebar} ${
            isLargeScreen ? styles.sidebarDesktop : 
            (showSidebar ? styles.sidebarOpen : styles.sidebarClosed)
          }`}
        >          <div className={`${styles.sidebarHeader} ${isLargeScreen ? styles.sidebarHeaderDesktop : ''}`}>
            <h1 className={styles.sidebarTitle}>Axioma Hábitos</h1>
            <button
              onClick={() => setShowSidebar(false)}
              className={`${styles.closeButton} ${isLargeScreen ? styles.closeButtonHidden : ''}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>          <nav className={styles.nav}>
            <button 
              className={`${styles.navButton} ${styles.navButtonActive}`}
              onClick={() => setShowSidebar(false)}
            >
              <Calendar className={styles.navIcon} />
              Hoje
            </button>            <button 
              className={`${styles.navButton} ${styles.navButtonInactive}`}
              onClick={() => {
                setShowAddModal(true);
                setShowSidebar(false);
              }}
            >
              <Plus className={styles.navIcon} />
              Adicionar Hábito
            </button>            <button 
              className={`${styles.navButton} ${styles.navButtonInactive}`}
              onClick={() => {
                setShowTour(true);
                setShowSidebar(false);
              }}
            >
              <HelpCircle className={styles.navIcon} />
              Como Usar
            </button>
            {/* botão de testes detalhes dos habitos */}
            {/* <button 
              className={`${styles.navButton} ${styles.navButtonInactive}`}
              onClick={() => {
                setShowHabitDetailsTour(true);
                setShowSidebar(false);
              }}
            >
              <Target className={styles.navIcon} />
              Guia: Detalhes do Hábito
            </button> */}
            <div className={styles.navDivider}>
              <p className={styles.navLabel}>Em breve</p>
              <button className={`${styles.navButton} ${styles.navButtonDisabled}`}>
                <Trophy className={styles.navIcon} />
                Conquistas
              </button>
              <button className={`${styles.navButton} ${styles.navButtonDisabled}`}>
                <Settings className={styles.navIcon} />
                Configurações
              </button>
            </div>
          </nav>          <div className={styles.userProfile}>
            <div className={styles.userInfo}>
              <div className={styles.userAvatar}>
                {userProfile?.name?.[0]?.toUpperCase()}
              </div>
              <div className={styles.userDetails}>
                <p className={styles.userName}>{userProfile?.name}</p>
                <p className={styles.userLevel}>Nível {userProfile?.fitnessLevel}</p>
              </div>            </div>
            <button
              onClick={logout}
              className={styles.logoutButton}
            >              <LogOut className={styles.logoutIcon} />
              Sair
            </button>
          </div></aside>

        {/* Overlay for mobile */}
        {showSidebar && !isLargeScreen && (
          <div
            className={styles.overlay}
            onClick={() => setShowSidebar(false)}
          />
        )}        {/* Main Content */}
        <div className={`${styles.mainContent} ${isLargeScreen ? styles.mainContentDesktop : ''} ${isAnyModalOpen ? styles.mainContentModalOpen : ''}`}>
          <div className="max-w-4xl mx-auto">            {/* Header */}
            <div className={styles.welcomeSection}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4"
              >
                <h2 className={styles.greeting}>
                  {getGreeting()}, {userProfile?.name?.split(' ')[0]}! 👋
                </h2>
                <p className={styles.dateDisplay}>{todayFormatted}</p>
              </motion.div>              {/* Stats Cards */}
              <div className={styles.statsGrid}>                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className={styles.statCard}
                  title="Hábitos completados hoje"
                  data-tour="stats-completed"
                >
                  <div className={styles.statContent}>
                    <div 
                      className={`${styles.statIcon} ${styles.statIconGreen}`}
                      title="Hábitos completados hoje"
                    >
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    </div>                    <div className={styles.statDetails}>
                      <p className={styles.statValue}>{completedToday}</p>
                      <div className={styles.statLabel}>
                        Completados hoje
                        {isLargeScreen && (
                          <HelpTooltip
                            title="Hábitos Completados"
                            description="Cada ✓ que você marca libera dopamina e reforça o comportamento. Celebre cada pequena vitória!"
                            tip="Marque imediatamente após concluir para recompensa instantânea."
                            position="bottom"
                            className={styles.helpIcon}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className={styles.statCard}
                  title="Dias consecutivos mantendo hábitos"
                  data-tour="stats-streak"
                >
                  <div className={styles.statContent}>
                    <div 
                      className={`${styles.statIcon} ${styles.statIconOrange}`}
                      title="Dias consecutivos mantendo hábitos"
                    >
                      <Flame className="w-6 h-6 text-orange-600" />
                    </div>                    <div className={styles.statDetails}>
                      <p className={styles.statValue}>{currentStreak}</p>
                      <div className={styles.statLabel}>
                        Dias em sequência
                        {isLargeScreen && (
                          <HelpTooltip
                            title="Sequência de Dias"
                            description="Quanto maior a sequência, mais forte se torna o hábito automático. Não quebre a corrente!"
                            tip="Se falhar um dia, recomece imediatamente no próximo. Consistência > Perfeição."
                            position="bottom"
                            className={styles.helpIcon}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className={styles.statCard}
                  title="Porcentagem de hábitos concluídos"
                  data-tour="stats-rate"
                >
                  <div className={styles.statContent}>
                    <div 
                      className={`${styles.statIcon} ${styles.statIconBlue}`}
                      title="Porcentagem de hábitos concluídos"
                    >
                      <Target className="w-6 h-6 text-blue-600" />
                    </div>                    <div className={styles.statDetails}>
                      <p className={styles.statValue}>{Math.round(completionPercentage)}%</p>
                      <div className={styles.statLabel}>
                        Taxa de conclusão
                        {isLargeScreen && (
                          <HelpTooltip
                            title="Taxa de Conclusão"
                            description="Meta: 80% ou mais para mudança real. Foque na consistência, não na perfeição."
                            tip="80% consistente é melhor que 100% esporádico."
                            position="bottom"
                            className={styles.helpIcon}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>{/* Progress Bar */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={styles.progressCard}
              >
                <div className={styles.progressHeader}>
                  <h3 className={styles.progressTitle}>Progresso do Dia</h3>
                  <span className={styles.progressCounter}>{completedToday}/{totalItems}</span>
                </div>
                <div className={styles.progressBarContainer}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${completionPercentage}%` }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                    className={styles.progressBarFill}
                  />
                </div>
              </motion.div>
            </div>

            {/* Daily Agenda */}
            <div className={styles.habitsSection}>              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Agenda do Dia</h3>
                <div className={styles.addButtonContainer}>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className={styles.addButton}
                    data-tour="add-habit"
                  >
                    <Plus className={styles.addIcon} />
                    Adicionar
                  </button>
                  
                  
                  {isLargeScreen && (
                    <HelpTooltip
                      title="Criar Hábito Atômico"
                      description="Comece pequeno! Em vez de 'exercitar 1 hora', tente 'fazer 5 flexões'. Use a regra dos 2 minutos."
                      tip="Qualquer hábito pode ser iniciado em menos de 2 minutos."
                      position="left"
                      className={styles.helpIcon}
                    />
                  )}
                </div>
              </div><div className={styles.agendaCard}>
                <div className={styles.agendaContent}>
                  {agenda.length === 0 ? (
                    <div className={styles.emptyState}>
                      <Circle className={styles.emptyStateIcon} />
                      <h4 className={styles.emptyStateTitle}>Nenhum hábito ainda</h4>
                      <p className={styles.emptyStateText}>Comece adicionando seu primeiro hábito!</p>
                      <button
                        onClick={() => setShowAddModal(true)}
                        className={styles.emptyStateButton}
                      >
                        Adicionar Hábito
                      </button>
                    </div>
                  ) : (
                    <div className={styles.habitsList}>
                      {agenda.map((item, index) => (                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}                          className={`${styles.habitItem} ${
                            item.isCompleted ? styles.habitItemCompleted : styles.habitItemPending
                          }`}
                          data-tour={index === 0 ? "habit-item" : undefined}
                        >{/* Botão de Check melhorado */}                          <button
                            onClick={async () => {
                              // Prevenir múltiplos cliques durante completar
                              if (isCompletingHabit && !item.isCompleted) return;
                              
                              try {
                                console.log('🟡 Dashboard: Click no botão de check para:', item.type, item.isCompleted ? 'desmarcar' : 'marcar');
                                
                                if (item.isCompleted) {
                                  // Desmarcar hábito
                                  if (item.type === 'habit') {
                                    console.log('🟡 Dashboard: Chamando uncompleteHabit para:', item.habit!.name);
                                    await uncompleteHabit(item.habit!.id);
                                    console.log('✅ Dashboard: uncompleteHabit finalizado');
                                  }
                                } else {
                                  // Marcar hábito
                                  if (item.type === 'habit') {
                                    console.log('🟡 Dashboard: Marcar hábito:', item.habit!.name);
                                    openCompleteModal(item.habit!);
                                  } else {
                                    console.log('🟡 Dashboard: Marcar stack:', item.stack!.name);
                                    handleCompleteHabit(item.stack!.newHabit);
                                  }
                                }
                              } catch (error) {
                                console.error('❌ Dashboard: Erro ao alterar status do hábito:', error);
                              }
                            }}
                            className={`${styles.habitCheckButton} ${
                              item.isCompleted ? styles.habitCheckButtonCompleted : styles.habitCheckButtonPending
                            }`}
                          >
                            {item.isCompleted ? (
                              <CheckCircle2 className={styles.habitCheckIcon} />
                            ) : (
                              <Circle className={styles.habitCheckIcon} />
                            )}
                          </button>

                          <div 
                            className={styles.habitDetails}
                            onClick={() => {
                              if (item.type === 'habit') {
                                openHabitDetails(item.habit!);
                              }
                            }}
                          >
                            <h4 className={`${styles.habitName} ${
                              item.isCompleted ? styles.habitNameCompleted : styles.habitNamePending
                            }`}>
                              {item.name}
                            </h4>
                            {item.description && (
                              <p className={styles.habitDescription}>
                                {item.description}
                              </p>
                            )}
                          </div>

                          <div className={styles.habitDuration}>
                            {item.duration} min
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}                </div>
              </div>
            </div>
          </div>
        </div>
      </div>      {/* Create Habit Modal */}
      <CreateHabitModal 
        isOpen={showAddModal} 
        onClose={() => setShowAddModal(false)} 
      />      {/* Habit Details Modal */}
      {selectedHabit && (
        <HabitDetailsModal
          habit={selectedHabit}
          isOpen={showHabitDetails}
          onClose={() => setShowHabitDetails(false)}
          onDelete={() => {
            setShowHabitDetails(false);
            setSelectedHabit(null);
            // A lista será atualizada automaticamente pelo contexto
          }}
          onOpenTour={() => {
            setShowHabitDetailsTour(true);
          }}
        />
      )}

      {/* Complete Habit Modal */}
      {habitToComplete && (
        <CompleteHabitModal
          habit={habitToComplete}
          isOpen={showCompleteModal}
          onClose={() => setShowCompleteModal(false)}
          onComplete={(customValue, notes) => handleCompleteHabit(habitToComplete, customValue, notes)}
        />
      )}      {/* Success Toast */}
      <SuccessToast 
        isVisible={showSuccessToast} 
        habitName={successHabitName}
        onClose={() => setShowSuccessToast(false)}
      />        {/* App Tour */}
      <AppTour 
        isActive={showTour}
        onComplete={() => setShowTour(false)}
      />
      
      {/* Habit Details Tour */}
      <HabitDetailsTour 
        isActive={showHabitDetailsTour}
        onComplete={() => setShowHabitDetailsTour(false)}
      />
    </div>
  );
};

export default Dashboard;
